import * as getTypes from '../get-types'
import * as actionTypes from '../action-types'
import * as mutationTypes from '../mutation-types'
import { ANONYMOUS_PLATFORM_IDENTITY_HASH } from '../constants/platform-constants'
import { RewardLinkAttributes, RewardLinkType } from '../constants/reward-link-constants'
import { RewardLinkStatus } from '@/cowboy/EnumTypes'
import { RewardLinkApi } from '@/cowboy/reward_link/RewardLinkApi'
import { RecipientApi } from '@/cowboy/recipient/RecipientApi'
import { SplitClient } from '@/plugins/split/SplitClient'
import { RX_DISABLE_DISB_LINK } from '@/plugins/split/ActiveSplits'
import { INJECTED_SERVICE_PROVIDER } from '@/store/modules/vuex_injection_provider'
import { StaticRewardLinkService } from '@/service/order_info/StaticRewardLinkService'

const state = {
  rewardLinkRecipientInfo: {
    isFirstTimeRecipient: false,
    firstRedemptionOccurredAt: null,
    lastRedemptionOccurredAt: null,
    totalRedemptions: 0,
    totalRedemptionsSinceNewRecipientCutoff: 0
  },
  rewardLink: null
}

const getters = {
  [getTypes.GET_REWARD_LINK]: state => state.rewardLink,
  [getTypes.GET_REWARD_LINK_BALANCE]: state => state.rewardLink?.availableAmount || null,
  [getTypes.GET_CURRENCY_CODE]: state => state.rewardLink?.currencyCode || null,
  [getTypes.GET_REWARD_CUSTOMIZATION]: state => state.rewardLink?.customization || null,
  [getTypes.GET_REWARD_CUSTOMIZATION_DETAILS]: state => state.rewardLink?.customization?.details || null,
  [getTypes.GET_REWARD_ELEMENT_CUSTOMIZATION_BY_SELECTOR]: state => selectorToFind => {
    const elementCustomizations = state.rewardLink?.customization?.details?.elementCustomizations
    return elementCustomizations?.find(element => element?.selector === selectorToFind) || {}
  },
  [getTypes.GET_REWARD_CUSTOMIZATION_ETID]: state => state.rewardLink?.customization?.emailTemplateIdentifier || null,
  [getTypes.GET_REWARD_LINK_EMAIL]: state => state.rewardLink?.metadata?.checkoutEmail?.trim() || '',
  [getTypes.GET_REWARD_LINK_FIRST_NAME]: state => state.rewardLink?.metadata?.checkoutFirstName?.trim() || '',
  [getTypes.GET_REWARD_LINK_LAST_NAME]: state => state.rewardLink?.metadata?.checkoutLastName?.trim() || '',
  [getTypes.REWARD_LINK_HAS_EXPIRATION_DATE]: state => !!(state.rewardLink && state.rewardLink.expirationDate),
  [getTypes.IS_PROMO_LINK]: (_, getters) => {
    const hasExpirationDate = getters[getTypes.REWARD_LINK_HAS_EXPIRATION_DATE]
    const { breakageRefundPercentage, daysToExpireAfterIssuance } =
      getters[getTypes.GET_REWARD_CUSTOMIZATION] || {}
    return !!(
      hasExpirationDate &&
      breakageRefundPercentage &&
      daysToExpireAfterIssuance
    )
  },
  [getTypes.TYPE_OF_REWARD_LINK]: (state, getters) => {
    if (getters[getTypes.IS_PROMO_LINK]) {
      return RewardLinkType.PROMO_LINK
    } else if (getters[getTypes.ORDER_INFO]?.attributes.includes(RewardLinkAttributes.IS_REDEEMABLE_FOR_CASH_ITEMS)) {
      return RewardLinkType.DISBURSEMENT_LINK
    }
    return RewardLinkType.REWARD_LINK
  },
  [getTypes.GET_IS_EXPIRED]: (state, getters) => {
    let retval = false
    if (getters[getTypes.REWARD_LINK_HAS_EXPIRATION_DATE]) {
      retval = new Date(state.rewardLink?.expirationDate) < new Date()
    }
    return retval
  },
  [getTypes.GET_IS_REDEEMED]: state => state.rewardLink?.isRedeemed || false,
  [getTypes.GET_IS_DL_AND_DL_REDEMPTIONS_DISABLED]: (state, getters) => {
    // RL-2429: Remove this whole getter and the getTypes constant
    const isDisbursementLink = getters[getTypes.TYPE_OF_REWARD_LINK] === RewardLinkType.DISBURSEMENT_LINK
    return isDisbursementLink && SplitClient.isSplitEnabled(RX_DISABLE_DISB_LINK)
  },
  [getTypes.GET_IS_LOCKED]: (state, getters) => RewardLinkStatus.LOCKED === state.rewardLink?.status || getters[getTypes.GET_IS_DL_AND_DL_REDEMPTIONS_DISABLED],
  [getTypes.GET_IS_FIRST_TIME_RECIPIENT]: state => state.rewardLinkRecipientInfo?.isFirstTimeRecipient || false,
  [getTypes.GET_RL_RECIPIENT_INFO]: state => state.rewardLinkRecipientInfo,

  [getTypes.GET_REWARD_LINK_PLATFORM_IDENTITY_HASH]: (_, getters) => getters[getTypes.ORDER_INFO]?.platformHash || ANONYMOUS_PLATFORM_IDENTITY_HASH,
  [getTypes.GET_REWARD_LINK_UTID]: (_, getters) => getters[getTypes.ORDER_INFO]?.utid || null,
  [getTypes.GET_REWARD_LINK_PARENT_REFERENCE_LINE_ITEM_ID]: (_, getters) => getters[getTypes.ORDER_INFO]?.referenceLineItemId || null,
  [getTypes.GET_IS_PRINTABLE_RL]: (_, getters) => getters[getTypes.STATIC_REWARD_LINK_SERVICE].isPrintable,
  [getTypes.ORDER_INFO]: (_, getters) => getters[getTypes.STATIC_REWARD_LINK_SERVICE].orderInfo,
  [getTypes.STATIC_REWARD_LINK_SERVICE]: (_, getters) => {
    return getters[INJECTED_SERVICE_PROVIDER](StaticRewardLinkService)
  }
}

const actions = {
  async [actionTypes.GET_REWARD_LINK_ASYNC] ({ commit, state }) {
    if (state.rewardLink) {
      return state.rewardLink
    } else {
      const responseData = await RewardLinkApi.fetchStoredValue()
      const data = responseData.toJSON()
      commit(mutationTypes.SET_REWARD_LINK, data)
      return data
    }
  },
  async [actionTypes.FETCH_REWARD_LINK_BALANCE] ({ dispatch, commit }) {
    const rewardLink = (await RewardLinkApi.fetchStoredValue()).toJSON()
    dispatch(actionTypes.UPDATE_BALANCE, rewardLink?.availableAmount)
    commit(mutationTypes.SET_IS_REDEEMED, rewardLink?.isRedeemed)
  },
  async [actionTypes.FETCH_SPECIFIC_RL_RECIPIENT_DATA] ({ commit }) {
    const recipientInfo = (await RecipientApi.fetchRecipientInfo()).toJSON()
    commit(mutationTypes.SET_SPECIFIC_RL_RECIPIENT_DATA, recipientInfo)
  },
  [actionTypes.UPDATE_REWARD_LINK_BALANCE] ({ dispatch, state }, amount) {
    const newBalance =
      parseFloat(state.rewardLink.availableAmount) + parseFloat(amount)
    dispatch(actionTypes.UPDATE_BALANCE, newBalance)
  },
  [actionTypes.UPDATE_BALANCE] ({ dispatch, commit }, newBalance) {
    newBalance < 0
      ? dispatch(actionTypes.RESET_CART)
      : commit(mutationTypes.SET_BALANCE, parseFloat(newBalance).toFixed(2))
  }
}

const mutations = {
  [mutationTypes.SET_REWARD_LINK_AVAILABLE_AMOUNT] (state, amount) {
    state.rewardLink.availableAmount = amount
  },
  [mutationTypes.SET_BALANCE] (state, balance) {
    state.rewardLink.availableAmount = parseFloat(balance).toFixed(2)
  },
  [mutationTypes.SET_IS_REDEEMED] (state, redeemed) {
    state.rewardLink.isRedeemed = redeemed
  },
  [mutationTypes.SET_REWARD_LINK] (state, rewardLink) {
    state.rewardLink = rewardLink
  },
  [mutationTypes.SET_SPECIFIC_RL_RECIPIENT_DATA] (
    state,
    rewardLinkRecipientInfo
  ) {
    const redemptionCounts = rewardLinkRecipientInfo?.redemptionCounts || {}

    state.rewardLinkRecipientInfo.isFirstTimeRecipient = !redemptionCounts.countOfRedemptionsSinceConfiguredCutoff
    state.rewardLinkRecipientInfo.firstRedemptionOccurredAt =
      redemptionCounts.firstRedemptionOccurredAt || null
    state.rewardLinkRecipientInfo.lastRedemptionOccurredAt =
      redemptionCounts.lastRedemptionOccurredAt || null
    state.rewardLinkRecipientInfo.totalRedemptions =
      redemptionCounts.countOfTotalRedemptions || 0
    state.rewardLinkRecipientInfo.totalRedemptionsSinceNewRecipientCutoff =
      redemptionCounts.countOfRedemptionsSinceConfiguredCutoff || 0
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
